import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import throttle from "lodash/throttle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import {
  SEARCH_PAYEE_ZONES,
  SearchPayeeZonesData,
  SearchPayeeZonesVars,
  ZoneOption,
} from "common/graphql/queries/SearchPayeeZones";
import { GET_ZONE_NAME, GetZoneNameData, GetZoneNameVars } from "common/graphql/queries/GetZoneName";

export interface ZoneAutocompleteProps {
  /** Payee account whose zones are searched */
  payeeAccountId: string;
  /** Selected zone's id, or null when nothing is selected */
  value: string | null;
  onChange: (zoneId: string | null) => void;
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  variant?: TextFieldProps["variant"];
  inputRef?: React.Ref<HTMLInputElement>;
}

/** Escapes ILIKE's wildcards so typed `%`/`_` match literally, then wraps for a substring match. */
export const toZoneSearchPattern = (input: string): string => `%${input.replace(/[\\%_]/g, "\\$&")}%`;

/**
 * Searchable single-select of a payee's zones (AUT-5863). Searches server-side as the user types
 * rather than loading every zone up front: app_user's zones permission caps any one query at 100
 * rows, and large clients have more zones than that. Takes and returns a bare zone id so it fits
 * anywhere an id is stored (a form, a URL-persisted grid filter); a selected id it hasn't seen in
 * a search yet (e.g. restored from a URL) gets its name looked up separately.
 */
export const ZoneAutocomplete: React.FC<ZoneAutocompleteProps> = ({
  payeeAccountId,
  value,
  onChange,
  label,
  disabled = false,
  size,
  variant,
  inputRef,
}) => {
  const [inputValue, setInputValue] = useState("");
  // The option the user last picked here, so selecting from the list needs no follow-up lookup.
  const [pickedOption, setPickedOption] = useState<ZoneOption | null>(null);

  const [searchZones, { data, previousData, loading, error }] = useLazyQuery<
    SearchPayeeZonesData,
    SearchPayeeZonesVars
  >(SEARCH_PAYEE_ZONES);

  // `value` is only a zone id, but the input box needs the zone's name. If the user picked the zone
  // from this list, pickedOption already has it. Otherwise -- the selection came in from outside, e.g.
  // a Zone filter restored from the URL on page load, or the filter panel reopened -- look the name up
  // by id. selectedOption below uses whichever source matches `value`.
  const { data: lookupData } = useQuery<GetZoneNameData, GetZoneNameVars>(GET_ZONE_NAME, {
    variables: { zoneIds: [value ?? ""] },
    skip: !value || pickedOption?.id === value,
  });

  const fetch = useMemo(() => throttle(searchZones, 200), [searchZones]);

  // Cancel any deferred call on unmount: a throttled search scheduled while the user was typing
  // would otherwise fire after the picker is gone.
  useEffect(() => () => fetch.cancel(), [fetch]);

  const search = (text: string) => {
    fetch({ variables: { payeeAccountId, search: toZoneSearchPattern(text) } });
  };

  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }
    if (pickedOption?.id === value) {
      return pickedOption;
    }
    return lookupData?.zones[0] ?? null;
  }, [value, pickedOption, lookupData]);

  // Keep the last results visible while the next search is in flight, and include the selected
  // zone so MUI can always match the value against an option.
  const options = useMemo(() => {
    const results = (data ?? previousData)?.zones ?? [];
    if (selectedOption && !results.some((zone) => zone.id === selectedOption.id)) {
      return [selectedOption, ...results];
    }
    return results;
  }, [data, previousData, selectedOption]);

  return (
    <Autocomplete<ZoneOption, false>
      fullWidth
      size={size}
      disabled={disabled}
      loading={loading}
      value={selectedOption}
      options={options}
      // Results are already filtered server-side; re-filtering here by the input text would also
      // hide everything but the selection once its name fills the input.
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_event, newValue) => {
        setPickedOption(newValue);
        onChange(newValue?.id ?? null);
      }}
      inputValue={inputValue}
      onOpen={() => search(inputValue)}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);
        // MUI fires "reset" when a selection rewrites the input to the chosen zone's name;
        // searching for the name we just picked is a wasted round-trip.
        if (reason !== "reset") {
          search(newInputValue);
        }
      }}
      noOptionsText={error ? "Couldn't load zones -- try refreshing" : "No matching zones"}
      renderInput={(params) => <TextField {...params} inputRef={inputRef} variant={variant} label={label} />}
    />
  );
};

export default ZoneAutocomplete;
